import { notification } from 'antd';
import { AxiosError, AxiosResponse } from 'axios';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient, UseQueryOptions } from 'react-query';
import { QueryFunctionContext } from 'react-query/types/core/types';

import { api } from './api-client';
import { pathToUrl } from './router';

type QueryKeyT = [string, object | undefined];

interface GetInfinitePagesInterface<T> {
  nextId?: number;
  previousId?: number;
  data: T;
  count: number;
}

export const getUrlWithId = (url: string, id?: string) => `${url}${id ? `/${id}` : ''}`;

export const getFetcher = <T,>({ queryKey, pageParam }: QueryFunctionContext<QueryKeyT>): Promise<T> => {
  const [url, params] = queryKey;
  return api.get<T>(url, { ...params, pageParam }).then((res) => res.data);
};

type GetFetchProps<T> = {
  url: string | null;
  params?: object;
  config?: UseQueryOptions<T, AxiosError<{ messages?: Record<string, string> }, unknown>, T, QueryKeyT>;
};

export const useGetFetch = <T,>({ url, params, config }: GetFetchProps<T>) => {
  const context = useQuery<T, AxiosError<{ messages?: Record<string, string> }, unknown>, T, QueryKeyT>(
    [url ?? '', params],
    ({ queryKey, meta = undefined }) => getFetcher({ queryKey, meta }),
    {
      // TODO: Configure proper stale time per query
      enabled: !!url,
      refetchOnWindowFocus: false,
      staleTime: 0,
      retry: false,
      ...config,
    }
  );

  return context;
};

export const postFetcher = <T,>({ queryKey }: QueryFunctionContext<QueryKeyT>): Promise<T> => {
  const [url, params] = queryKey;
  return api.post<T>(url, params).then((res) => res.data);
};

export const usePostFetch = <T,>(
  url: string | null,
  params?: object,
  config?: UseQueryOptions<T, AxiosError<{ messages?: Record<string, string> }, unknown>, T, QueryKeyT>
) => {
  const context = useQuery<T, AxiosError<{ messages?: Record<string, string> }, unknown>, T, QueryKeyT>(
    [url ?? '', params ?? {}],
    ({ queryKey, meta = undefined }) => postFetcher({ queryKey, meta }),
    {
      enabled: !!url,
      // TODO: Configure proper stale time per query
      // keepPreviousData: true,
      refetchOnWindowFocus: false,
      staleTime: 0,
      retry: false,
      ...config,
    }
  );

  return context;
};

export const useLoadMore = <T,>(url: string | null, params?: object) => {
  const context = useInfiniteQuery<GetInfinitePagesInterface<T>, Error, GetInfinitePagesInterface<T>, QueryKeyT>(
    [url ?? '', params],
    ({ queryKey, pageParam = 1, meta = undefined }) => getFetcher({ queryKey, pageParam, meta }),
    {
      getPreviousPageParam: (firstPage) => firstPage.previousId ?? false,
      getNextPageParam: (lastPage) => {
        return lastPage.nextId ?? false;
      },
    }
  );

  return context;
};

export const usePrefetch = <T,>(url: string | null, params?: object) => {
  const queryClient = useQueryClient();

  return () => {
    if (!url) {
      return;
    }

    queryClient.prefetchQuery<T, Error, T, QueryKeyT>([url, params], ({ queryKey, meta = undefined }) =>
      getFetcher({ queryKey, meta })
    );
  };
};

const useGenericMutation = <T, S>(
  func: ({ queryParams, data }: { queryParams?: object; data?: T | S }) => Promise<AxiosResponse<S>>,
  url: string,
  params?: object,
  onSuccess?: () => void,
  updater?: ((oldData: T, newData: S) => T) | undefined
) => {
  const queryClient = useQueryClient();
  return useMutation<
    AxiosResponse,
    AxiosError<{ messages?: Record<string, string> }, unknown>,
    { queryParams?: object; data?: T | S }
  >(func, {
    onMutate: async ({ data }) => {
      await queryClient.cancelQueries([url, params]);

      const previousData = queryClient.getQueryData([url, params]);
      queryClient.setQueryData<T>([url, params], (oldData) => {
        return updater ? updater(oldData as T, data as S) : (data as T);
      });

      return previousData;
    },
    // TODO: Add mapping for error fields and messages
    onError: (error, _, context) => {
      queryClient.setQueryData([url, params], context);
    },
    onSettled: () => {
      queryClient.invalidateQueries([url, params]);
    },
    onSuccess: (_data, variables) => {
      notification.success({
        message: 'Data saved successfully',
      });
      queryClient.invalidateQueries(pathToUrl(url, variables.queryParams));
      onSuccess?.();
    },
  });
};

type UseDeleteProps<T> = {
  url: string;
  params?: object;
  onSuccess?: () => void;
  updater?: (oldData: T, id: string | number) => T;
};

export const useDelete = <T,>({ url, params, onSuccess, updater }: UseDeleteProps<T>) => {
  return useGenericMutation<T, string | number>(
    ({ queryParams }) => api.delete(pathToUrl(url, queryParams)),
    url,
    params,
    onSuccess,
    updater
  );
};

type UseUpdateProps<T, S> = {
  url: string;
  params?: object;
  onSuccess?: () => void;
  updater?: (oldData: T, newData: S) => T;
};

export const usePost = <T, S>({ url, params, onSuccess, updater }: UseUpdateProps<T, S>) => {
  return useGenericMutation<T, S>(
    ({ queryParams, data }) => api.post<S>(pathToUrl(url, queryParams), data),
    url,
    params,
    onSuccess,
    updater
  );
};

export const usePatch = <T, S>({ url, params, onSuccess, updater }: UseUpdateProps<T, S>) => {
  return useGenericMutation<T, S>(
    ({ queryParams, data }) => api.patch<S>(pathToUrl(url, queryParams), data),
    url,
    params,
    onSuccess,
    updater
  );
};
